import { useState, useMemo, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as d3 from 'd3';

export interface DiscoveryStrategy {
  type: 'multicast' | 'broadcast' | 'DHT' | 'gossip' | 'active_probing';
  enabled: boolean;
  config: {
    interval: number;
    timeout: number;
    retries: number;
    [key: string]: any;
  };
  metrics: {
    nodesDiscovered: number;
    successRate: number;
    averageLatency: number;
    bandwidth: number;
  };
}

export interface DiscoveryEvent {
  id: string;
  timestamp: Date;
  type: 'node_discovered' | 'node_lost' | 'route_established' | 'route_failed' | 'heartbeat';
  sourceNode: string;
  targetNode?: string;
  strategy: string;
  latency?: number;
  success: boolean;
  metadata?: Record<string, any>;
}

export interface PeerConnection {
  id: string;
  sourceNode: string;
  targetNode: string;
  status: 'establishing' | 'established' | 'failed' | 'terminated';
  establishedAt?: Date;
  lastSeen: Date;
  route: string[];
  metrics: {
    rtt: number;
    bandwidth: number;
    reliability: number;
  };
  strategy: string;
}

export interface LoadBalancingConfig {
  algorithm: 'round_robin' | 'least_connections' | 'weighted' | 'consistent_hash' | 'response_time';
  weights?: Record<string, number>;
  enabled: boolean;
  metrics: {
    requestsDistributed: number;
    averageResponseTime: number;
    nodesInRotation: number;
  };
}

interface NetworkDiscoveryVisualizationProps {
  strategies: DiscoveryStrategy[];
  discoveryEvents: DiscoveryEvent[];
  peerConnections: PeerConnection[];
  loadBalancing: LoadBalancingConfig;
  selectedNode?: string;
  timeRange: {
    start: Date;
    end: Date;
  };
  onStrategyToggle: (strategyType: string, enabled: boolean) => void;
  onNodeSelect: (nodeId: string) => void;
}

/**
 * Discovery strategy configuration panel
 */
function DiscoveryStrategyPanel({
  strategies,
  onStrategyToggle,
}: {
  strategies: DiscoveryStrategy[];
  onStrategyToggle: (strategyType: string, enabled: boolean) => void;
}) {
  const getStrategyColor = (type: string) => {
    const colors = {
      multicast: '#3b82f6',
      broadcast: '#10b981', 
      DHT: '#8b5cf6',
      gossip: '#f59e0b',
      active_probing: '#ef4444',
    } as const;
    return colors[type as keyof typeof colors] || '#6b7280';
  };

  const getStrategyDescription = (type: string) => {
    const descriptions = {
      multicast: 'Efficient group communication using IP multicast',
      broadcast: 'Simple broadcast discovery on local network',
      DHT: 'Distributed Hash Table for peer discovery',
      gossip: 'Epidemic-style information propagation',
      active_probing: 'Direct connection attempts to known nodes',
    };
    return descriptions[type as keyof typeof descriptions] || 'Unknown strategy';
  };

  return (
    <div className="bg-gray-900 rounded-lg p-4">
      <h3 className="text-lg font-semibold text-white mb-4">Discovery Strategies</h3>
      
      <div className="space-y-4">
        {strategies.map((strategy) => (
          <motion.div
            key={strategy.type}
            className={`p-4 bg-gray-800 rounded-lg ${
              strategy.enabled ? 'status-active' : 'status-disabled'
            }`}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
          >
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <div 
                  className="w-4 h-4 rounded-full"
                  style={{ backgroundColor: getStrategyColor(strategy.type) }}
                />
                <span className="font-medium text-white capitalize">
                  {strategy.type.replace('_', ' ')}
                </span>
              </div>
              
              <button
                onClick={() => onStrategyToggle(strategy.type, !strategy.enabled)}
                className={`px-3 py-1 rounded text-sm transition-colors ${
                  strategy.enabled 
                    ? 'bg-status-online text-white hover:bg-status-online-dark' 
                    : 'bg-gray-600 text-gray-300 hover:bg-gray-500'
                }`}
              >
                {strategy.enabled ? 'Enabled' : 'Disabled'}
              </button>
            </div>

            <p className="text-sm text-gray-400 mb-3">
              {getStrategyDescription(strategy.type)}
            </p>

            {strategy.enabled && (
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-400">Nodes Discovered:</span>
                  <span className="text-primary-400 ml-2 font-mono">
                    {strategy.metrics.nodesDiscovered}
                  </span>
                </div>
                <div>
                  <span className="text-gray-400">Success Rate:</span>
                  <span className="text-status-online ml-2 font-mono">
                    {(strategy.metrics.successRate * 100).toFixed(1)}%
                  </span>
                </div>
                <div>
                  <span className="text-gray-400">Avg Latency:</span>
                  <span className="text-yellow-400 ml-2 font-mono">
                    {strategy.metrics.averageLatency.toFixed(0)}ms
                  </span>
                </div>
                <div>
                  <span className="text-gray-400">Bandwidth:</span>
                  <span className="text-secondary-400 ml-2 font-mono">
                    {(strategy.metrics.bandwidth / 1024).toFixed(1)} KB/s
                  </span>
                </div>
              </div>
            )}

            {strategy.enabled && (
              <div className="mt-3 pt-3 border-t border-gray-700">
                <div className="grid grid-cols-3 gap-2 text-xs">
                  <div>
                    <span className="text-gray-500">Interval:</span>
                    <span className="text-gray-300 ml-1">{strategy.config.interval}ms</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Timeout:</span>
                    <span className="text-gray-300 ml-1">{strategy.config.timeout}ms</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Retries:</span>
                    <span className="text-gray-300 ml-1">{strategy.config.retries}</span>
                  </div>
                </div>
              </div>
            )}
          </motion.div>
        ))}
      </div>
    </div>
  );
}

/**
 * Discovery timeline visualization
 */
function DiscoveryTimeline({
  events,
  timeRange,
  onNodeSelect,
  selectedNode: _selectedNode,
}: {
  events: DiscoveryEvent[];
  timeRange: { start: Date; end: Date };
  onNodeSelect: (nodeId: string) => void;
  selectedNode?: string;
}) {
  const svgRef = useRef<SVGSVGElement>(null);
  const [hoveredEvent, setHoveredEvent] = useState<DiscoveryEvent | null>(null);

  const filteredEvents = useMemo(() => {
    return events
      .filter(event => event.timestamp >= timeRange.start && event.timestamp <= timeRange.end)
      .sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
  }, [events, timeRange]);

  const eventsByStrategy = useMemo(() => {
    const grouped = new Map<string, DiscoveryEvent[]>();
    filteredEvents.forEach(event => {
      if (!grouped.has(event.strategy)) {
        grouped.set(event.strategy, []);
      }
      grouped.get(event.strategy)!.push(event);
    });
    return grouped;
  }, [filteredEvents]);

  const getEventColor = (type: string, success: boolean) => {
    if (!success) {
return '#ef4444';
}
    
    const colors = {
      node_discovered: '#10b981',
      node_lost: '#f59e0b',
      route_established: '#06b6d4',
      route_failed: '#ef4444',
      heartbeat: '#6b7280',
    };
    return colors[type as keyof typeof colors] || '#6b7280';
  };

  useEffect(() => {
    if (!svgRef.current || eventsByStrategy.size === 0) {
return;
}

    const svg = d3.select(svgRef.current);
    const margin = { top: 20, right: 40, bottom: 40, left: 100 };
    const width = 800 - margin.left - margin.right;
    const height = 400 - margin.top - margin.bottom;

    svg.selectAll('*').remove();

    const g = svg.append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);

    // Time scale
    const timeScale = d3.scaleTime()
      .domain([timeRange.start, timeRange.end])
      .range([0, width]);

    // Strategy scale (vertical lanes)
    const strategies = Array.from(eventsByStrategy.keys());
    const strategyScale = d3.scaleBand()
      .domain(strategies)
      .range([0, height])
      .padding(0.1);

    // Draw timeline axis
    const timeAxis = d3.axisBottom(timeScale)
      .tickFormat((d) => d3.timeFormat('%H:%M:%S')(d as Date));

    g.append('g')
      .attr('transform', `translate(0,${height})`)
      .call(timeAxis)
      .selectAll('text')
      .attr('fill', '#9ca3af');

    // Draw strategy lanes
    strategies.forEach(strategy => {
      const y = strategyScale(strategy)!;
      const laneHeight = strategyScale.bandwidth();

      // Lane background
      g.append('rect')
        .attr('x', 0)
        .attr('y', y)
        .attr('width', width)
        .attr('height', laneHeight)
        .attr('fill', '#374151')
        .attr('opacity', 0.3);

      // Lane label
      g.append('text')
        .attr('x', -10)
        .attr('y', y + laneHeight / 2)
        .attr('text-anchor', 'end')
        .attr('dominant-baseline', 'middle')
        .attr('fill', '#e5e7eb')
        .attr('font-size', '12px')
        .text(strategy);

      // Draw events in this lane
      const strategyEvents = eventsByStrategy.get(strategy) || [];
      
      g.selectAll(`.event-${strategy}`)
        .data(strategyEvents)
        .enter()
        .append('circle')
        .attr('class', `event-${strategy}`)
        .attr('cx', d => timeScale(d.timestamp))
        .attr('cy', y + laneHeight / 2)
        .attr('r', 4)
        .attr('fill', d => getEventColor(d.type, d.success))
        .attr('stroke', '#000000')
        .attr('stroke-width', 1)
        .style('cursor', 'pointer')
        .on('mouseover', (_event, d) => setHoveredEvent(d))
        .on('mouseout', () => setHoveredEvent(null))
        .on('click', (_event, d) => {
          if (d.targetNode) {
onNodeSelect(d.targetNode);
} else {
onNodeSelect(d.sourceNode);
}
        });
    });

  }, [eventsByStrategy, timeRange, onNodeSelect]);

  return (
    <div className="bg-gray-900 rounded-lg p-4">
      <h3 className="text-lg font-semibold text-white mb-4">Discovery Timeline</h3>
      
      <div className="relative">
        <svg
          ref={svgRef}
          width="800"
          height="400"
          className="bg-gray-800 rounded"
        />
        
        {/* Event tooltip */}
        <AnimatePresence>
          {hoveredEvent && (
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              className="absolute bottom-4 left-4 bg-black/90 rounded-lg p-3 text-white text-sm cursor-pointer"
              onClick={() => setHoveredEvent(null)}
            >
              <div className="font-semibold text-primary-300">{hoveredEvent.type.replace('_', ' ')}</div>
              <div className="space-y-1 mt-2">
                <div>Time: <span className="text-yellow-400">
                  {hoveredEvent.timestamp.toLocaleTimeString()}
                </span></div>
                <div>Source: <span className="text-status-online">{hoveredEvent.sourceNode}</span></div>
                {hoveredEvent.targetNode && (
                  <div>Target: <span className="text-primary-400">{hoveredEvent.targetNode}</span></div>
                )}
                <div>Strategy: <span className="text-secondary-400">{hoveredEvent.strategy}</span></div>
                {hoveredEvent.latency && (
                  <div>Latency: <span className="text-primary-400">{hoveredEvent.latency}ms</span></div>
                )}
                <div>Success: <span className={hoveredEvent.success ? 'text-status-online' : 'text-danger-400'}>
                  {hoveredEvent.success ? 'Yes' : 'No'}
                </span></div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Event type legend */}
      <div className="mt-4 flex flex-wrap gap-4 text-xs">
        {[
          { type: 'node_discovered', label: 'Node Discovered' },
          { type: 'node_lost', label: 'Node Lost' },
          { type: 'route_established', label: 'Route Established' },
          { type: 'route_failed', label: 'Route Failed' },
          { type: 'heartbeat', label: 'Heartbeat' },
        ].map(({ type, label }) => (
          <div key={type} className="flex items-center gap-2">
            <div 
              className="w-3 h-3 rounded-full border border-black"
              style={{ backgroundColor: getEventColor(type, true) }}
            />
            <span className="text-gray-400">{label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Peer connection establishment flow
 */
function PeerConnectionFlow({
  connections,
  onNodeSelect,
  selectedNode,
}: {
  connections: PeerConnection[];
  onNodeSelect: (nodeId: string) => void;
  selectedNode?: string;
}) {
  const connectionStats = useMemo(() => {
    const stats = {
      total: connections.length,
      establishing: connections.filter(c => c.status === 'establishing').length,
      established: connections.filter(c => c.status === 'established').length,
      failed: connections.filter(c => c.status === 'failed').length,
      terminated: connections.filter(c => c.status === 'terminated').length,
      avgRtt: connections.reduce((sum, c) => sum + c.metrics.rtt, 0) / connections.length || 0,
      avgReliability: connections.reduce((sum, c) => sum + c.metrics.reliability, 0) / connections.length || 0,
    };
    return stats;
  }, [connections]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'established': return '#10b981';
      case 'establishing': return '#f59e0b';
      case 'failed': return '#ef4444';
      case 'terminated': return '#6b7280';
      default: return '#374151';
    }
  };

  return (
    <div className="bg-gray-900 rounded-lg p-4">
      <h3 className="text-lg font-semibold text-white mb-4">Peer Connections</h3>
      
      {/* Connection stats */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <div className="bg-gray-800 rounded-lg p-3 text-center">
          <div className="text-2xl font-bold text-primary-400">{connectionStats.total}</div>
          <div className="text-sm text-gray-400">Total</div>
        </div>
        <div className="bg-gray-800 rounded-lg p-3 text-center">
          <div className="text-2xl font-bold text-status-online">{connectionStats.established}</div>
          <div className="text-sm text-gray-400">Established</div>
        </div>
        <div className="bg-gray-800 rounded-lg p-3 text-center">
          <div className="text-2xl font-bold text-yellow-400">{connectionStats.establishing}</div>
          <div className="text-sm text-gray-400">Establishing</div>
        </div>
        <div className="bg-gray-800 rounded-lg p-3 text-center">
          <div className="text-2xl font-bold text-danger-400">{connectionStats.failed}</div>
          <div className="text-sm text-gray-400">Failed</div>
        </div>
      </div>

      {/* Connection list */}
      <div className="space-y-2 max-h-96 overflow-y-auto">
        {connections
          .sort((a, b) => {
            // Sort by status priority, then by establishment time
            const statusPriority = { establishing: 3, established: 2, failed: 1, terminated: 0 };
            const aPriority = statusPriority[a.status as keyof typeof statusPriority] || 0;
            const bPriority = statusPriority[b.status as keyof typeof statusPriority] || 0;
            
            if (aPriority !== bPriority) {
return bPriority - aPriority;
}
            return b.lastSeen.getTime() - a.lastSeen.getTime();
          })
          .map((connection, index) => (
            <motion.div
              key={connection.id}
              className={`bg-gray-800 rounded-lg p-3 cursor-pointer hover:bg-gray-700 transition-colors ${
                selectedNode === connection.sourceNode || selectedNode === connection.targetNode
                  ? 'bg-blue-500/10'
                  : ''
              }`}
              onClick={() => onNodeSelect(connection.targetNode)}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className="text-white font-medium">
                    {connection.sourceNode.slice(-8)} → {connection.targetNode.slice(-8)}
                  </span>
                  <span 
                    className="px-2 py-1 rounded text-xs text-white"
                    style={{ backgroundColor: getStatusColor(connection.status) }}
                  >
                    {connection.status.toUpperCase()}
                  </span>
                </div>
                <span className="text-gray-400 text-sm">
                  {connection.strategy}
                </span>
              </div>

              {connection.status === 'established' && (
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <span className="text-gray-400">RTT:</span>
                    <span className="text-primary-400 ml-2">{connection.metrics.rtt.toFixed(0)}ms</span>
                  </div>
                  <div>
                    <span className="text-gray-400">Bandwidth:</span>
                    <span className="text-status-online ml-2">
                      {(connection.metrics.bandwidth / 1024).toFixed(1)} KB/s
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-400">Reliability:</span>
                    <span className="text-yellow-400 ml-2">
                      {(connection.metrics.reliability * 100).toFixed(1)}%
                    </span>
                  </div>
                </div>
              )}

              {connection.route.length > 2 && (
                <div className="mt-2 text-xs text-gray-400">
                  Route: {connection.route.map(node => node.slice(-4)).join(' → ')}
                </div>
              )}

              <div className="mt-2 text-xs text-gray-500">
                Last seen: {connection.lastSeen.toLocaleString()}
              </div>
            </motion.div>
          ))
        }
      </div>
    </div>
  );
}

/**
 * Load balancing visualization
 */
function LoadBalancingVisualization({
  loadBalancing,
}: {
  loadBalancing: LoadBalancingConfig;
}) {
  const getAlgorithmDescription = (algorithm: string) => {
    const descriptions = {
      round_robin: 'Requests distributed equally in rotation',
      least_connections: 'Route to node with fewest active connections',
      weighted: 'Distribution based on configured node weights',
      consistent_hash: 'Hash-based routing for session affinity',
      response_time: 'Route to fastest responding nodes',
    };
    return descriptions[algorithm as keyof typeof descriptions] || 'Unknown algorithm';
  };

  return (
    <div className="bg-gray-900 rounded-lg p-4">
      <h3 className="text-lg font-semibold text-white mb-4">Load Balancing</h3>
      
      <div className={`p-4 rounded-lg ${
        loadBalancing.enabled ? 'bg-status-online/20' : 'bg-gray-800'
      }`}>
        <div className="flex items-center justify-between mb-3">
          <span className="font-medium text-white">
            {loadBalancing.algorithm.replace('_', ' ').toUpperCase()}
          </span>
          <span className={`px-3 py-1 rounded text-sm ${
            loadBalancing.enabled 
              ? 'bg-status-online text-white' 
              : 'bg-gray-600 text-gray-300'
          }`}>
            {loadBalancing.enabled ? 'Enabled' : 'Disabled'}
          </span>
        </div>

        <p className="text-sm text-gray-400 mb-4">
          {getAlgorithmDescription(loadBalancing.algorithm)}
        </p>

        {loadBalancing.enabled && (
          <div className="grid grid-cols-3 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-primary-400">
                {loadBalancing.metrics.requestsDistributed.toLocaleString()}
              </div>
              <div className="text-sm text-gray-400">Requests Distributed</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-status-online">
                {loadBalancing.metrics.averageResponseTime.toFixed(0)}ms
              </div>
              <div className="text-sm text-gray-400">Avg Response Time</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-yellow-400">
                {loadBalancing.metrics.nodesInRotation}
              </div>
              <div className="text-sm text-gray-400">Nodes in Rotation</div>
            </div>
          </div>
        )}

        {loadBalancing.weights && Object.keys(loadBalancing.weights).length > 0 && (
          <div className="mt-4 pt-4 border-t border-gray-700">
            <div className="text-sm font-medium text-gray-300 mb-2">Node Weights</div>
            <div className="grid grid-cols-2 gap-2 text-xs">
              {Object.entries(loadBalancing.weights).map(([nodeId, weight]) => (
                <div key={nodeId} className="flex justify-between">
                  <span className="text-gray-400">{nodeId.slice(-8)}</span>
                  <span className="text-primary-400">{weight}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Main network discovery and routing visualization component
 */
export function NetworkDiscoveryVisualization({
  strategies,
  discoveryEvents,
  peerConnections,
  loadBalancing,
  selectedNode,
  timeRange,
  onStrategyToggle,
  onNodeSelect,
}: NetworkDiscoveryVisualizationProps) {
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-white">Network Discovery & Routing</h2>
        <div className="text-sm text-gray-400">
          Time Range: {timeRange.start.toLocaleTimeString()} - {timeRange.end.toLocaleTimeString()}
        </div>
      </div>

      {/* Top row - Discovery strategies and load balancing */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <div className="xl:col-span-2">
          <DiscoveryStrategyPanel
            strategies={strategies}
            onStrategyToggle={onStrategyToggle}
          />
        </div>
        <LoadBalancingVisualization loadBalancing={loadBalancing} />
      </div>

      {/* Middle row - Discovery timeline */}
      <DiscoveryTimeline
        events={discoveryEvents}
        timeRange={timeRange}
        onNodeSelect={onNodeSelect}
        selectedNode={selectedNode}
      />

      {/* Bottom row - Peer connections */}
      <PeerConnectionFlow
        connections={peerConnections}
        onNodeSelect={onNodeSelect}
        selectedNode={selectedNode}
      />
    </div>
  );
}

export default NetworkDiscoveryVisualization;